import React, { FC, useCallback, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  APP_LAYOUT_BASE,
  PREMIUM_SERIES_INNER_BASE,
  PREMIUM_UPLOAD_VIDEO_WORKSPACE_PATH,
  PREMIUM_VOICE_DESIGN_FULL_PATH,
  PREMIUM_VOICE_LIBRARY_PUBLIC_PATH,
  QUICK_SERIES_INNER_BASE,
} from "../../utils/menuPathNormalize";
import { PremiumWorkspaceListMetaContext } from "./premiumWorkspaceContext";
import "./homeSeriesContent.scss";

function pathnameIsPremiumSeries(pathname: string): boolean {
  const base = PREMIUM_SERIES_INNER_BASE;
  return pathname === base || pathname.startsWith(`${base}/`);
}

function pathnameIsPremiumUploadVideoWorkspace(pathname: string): boolean {
  return pathname === PREMIUM_UPLOAD_VIDEO_WORKSPACE_PATH;
}

function pathnameIsQuickSeriesInner(pathname: string): boolean {
  const base = QUICK_SERIES_INNER_BASE;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * 精品译制工作区：页面级导航（返回 / 标题 / meta）已随 Header 下沉到各子页自行渲染，
 * 本 layout 仅承担 workspace 级列表元信息上下文 + 滚动壳层。
 */
export const PremiumWorkspaceLayout: FC = () => {
  const location = useLocation();
  const [listTotalCount, setListTotalCountState] = useState(0);

  const setListTotalCount = useCallback((n: number) => {
    setListTotalCountState(Number.isFinite(n) ? n : 0);
  }, []);

  const listMetaApi = useMemo(
    () => ({ setListTotalCount }),
    [setListTotalCount],
  );

  const isPremiumInner = pathnameIsPremiumSeries(location.pathname);
  const isQuickInner = pathnameIsQuickSeriesInner(location.pathname);
  const isInner = isPremiumInner || isQuickInner;
  const isPremiumUploadVideoWorkspace = pathnameIsPremiumUploadVideoWorkspace(
    location.pathname,
  );
  const isVoiceDesignWorkspace =
    location.pathname === PREMIUM_VOICE_DESIGN_FULL_PATH;
  const isVoiceCloneWorkspace =
    location.pathname === `${APP_LAYOUT_BASE}/voice-clone`;
  const isSubtitleTransWorkspace =
    location.pathname === `${APP_LAYOUT_BASE}/subtitle-trans`;
  const isTextToAudioWorkspace =
    location.pathname === `${APP_LAYOUT_BASE}/text-to-audio`;
  const isVoiceAiWorkspace =
    location.pathname === `${APP_LAYOUT_BASE}/ai-voice`;
  const isAiImageWorkspace = location.pathname.startsWith(
    `${APP_LAYOUT_BASE}/ai-image`,
  );
  const isPodcastWorkspace = location.pathname === `${APP_LAYOUT_BASE}/podcast`;
  // AI 视频：使用 startsWith 让 /hub/ai-video/detail 也命中主标题"AI 视频"
  const isAiVideoWorkspace = location.pathname.startsWith(
    `${APP_LAYOUT_BASE}/ai-video`,
  );
  const isAiDigitalHumanWorkspace = location.pathname.startsWith(
    `${APP_LAYOUT_BASE}/ai-digital-human`,
  );
  const isVoiceLibraryPublicIndex =
    location.pathname === PREMIUM_VOICE_LIBRARY_PUBLIC_PATH;
  const isTranscriptionWorkspace =
    location.pathname === `${APP_LAYOUT_BASE}/transcription`;

  const scrollClassName = useMemo(() => {
    const isFlushWorkspace =
      isVoiceDesignWorkspace ||
      isVoiceCloneWorkspace ||
      isSubtitleTransWorkspace ||
      isTextToAudioWorkspace ||
      isVoiceAiWorkspace ||
      isAiImageWorkspace ||
      isPodcastWorkspace ||
      isAiVideoWorkspace ||
      isAiDigitalHumanWorkspace ||
      isVoiceLibraryPublicIndex ||
      isTranscriptionWorkspace;
    const isWhiteFlushWorkspace =
      isVoiceAiWorkspace ||
      isAiImageWorkspace ||
      isPodcastWorkspace ||
      isAiVideoWorkspace ||
      isAiDigitalHumanWorkspace ||
      isVoiceLibraryPublicIndex ||
      isTranscriptionWorkspace;

    return [
      "home-series__scroll",
      isInner || isPremiumUploadVideoWorkspace
        ? "premium-series-inner__scroll"
        : "",
      isPremiumUploadVideoWorkspace
        ? "home-series__scroll--upload-workspace"
        : "",
      isFlushWorkspace ? "home-series__scroll--flush" : "",
      isWhiteFlushWorkspace ? "home-series__scroll--flush-white" : "",
    ]
      .filter(Boolean)
      .join(" ");
  }, [
    isInner,
    isPremiumUploadVideoWorkspace,
    isVoiceDesignWorkspace,
    isVoiceCloneWorkspace,
    isSubtitleTransWorkspace,
    isTextToAudioWorkspace,
    isVoiceAiWorkspace,
    isAiImageWorkspace,
    isPodcastWorkspace,
    isAiVideoWorkspace,
    isAiDigitalHumanWorkspace,
    isVoiceLibraryPublicIndex,
    isTranscriptionWorkspace,
  ]);

  return (
    <PremiumWorkspaceListMetaContext.Provider value={listMetaApi}>
      <div className="home-series">
        <div className={scrollClassName}>
          <Outlet />
        </div>
      </div>
    </PremiumWorkspaceListMetaContext.Provider>
  );
};
